import { Fragment, useMemo } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, CheckIcon, SparklesIcon, RocketLaunchIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchPublicPlans } from '../../lib/apiClient';
import { pricingPlans } from '../../lib/marketing';
import type { Plan } from '../../types';

interface PaywallModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message?: string;
  currentUsage?: {
    reportsUsed: number;
    reportsLimit: number;
  };
}

const currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

export function PaywallModal({
  isOpen,
  onClose,
  title = 'Upgrade to Continue',
  message = 'You\'ve used your free report. Upgrade to generate unlimited professional inspection reports.',
  currentUsage,
}: PaywallModalProps): JSX.Element {
  const navigate = useNavigate();
  const { data: publicPlans = [] } = useQuery({
    queryKey: ['public-plans'],
    queryFn: fetchPublicPlans,
    staleTime: 1000 * 60 * 10,
  });

  const fallbackPlans: Plan[] = useMemo(() => {
    return pricingPlans.map((plan) => ({
      id: plan.name,
      slug: plan.price === 'Free trial'
        ? 'trial'
        : plan.name === 'Enterprise'
        ? 'custom'
        : plan.name.toLowerCase(),
      name: plan.name,
      description: plan.description,
      priceMonthly: plan.price === 'Free trial' || plan.price === 'Talk to us' ? null : Number(plan.price.replace(/[^0-9.]/g, '')),
      currency: 'USD',
      features: plan.perks,
      trialDays: plan.price === 'Free trial' ? 30 : 0,
      isPublic: true,
      isCustom: plan.name === 'Enterprise',
    }));
  }, []);

  const plans = useMemo(() => {
    const basePlans = publicPlans.length > 0 ? publicPlans : fallbackPlans;
    return basePlans.filter((plan) => (plan.priceMonthly != null && plan.priceMonthly > 0) || plan.isCustom);
  }, [fallbackPlans, publicPlans]);

  const handleUpgrade = (plan: Plan) => {
    onClose();
    if (plan.isCustom || plan.priceMonthly == null) {
      window.open('mailto:raykundan57@gmail.com?subject=InspectAI%20custom%20plan', '_blank', 'noreferrer');
      return;
    }
    navigate(`/app/billing?plan=${plan.slug}`);
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-3xl glass-card p-0 shadow-2xl transition-all">
                {/* Header */}
                <div className="relative bg-gradient-to-r from-primary/20 to-accent/20 px-8 py-6">
                  <button
                    onClick={onClose}
                    className="absolute right-4 top-4 rounded-full p-2 text-muted-foreground hover:bg-white/20 hover:text-foreground transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                  
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/20">
                      <RocketLaunchIcon className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <Dialog.Title className="text-xl font-bold text-foreground">
                        {title}
                      </Dialog.Title>
                      <p className="text-sm text-muted-foreground">{message}</p>
                    </div>
                  </div>

                  {currentUsage && (
                    <div className="mt-4 rounded-xl bg-white/10 p-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Reports used this month</span>
                        <span className="font-semibold text-foreground">
                          {currentUsage.reportsUsed} / {currentUsage.reportsLimit}
                        </span>
                      </div>
                      <div className="mt-2 h-2 rounded-full bg-white/20">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-primary to-accent transition-all"
                          style={{
                            width: `${Math.min(100, (currentUsage.reportsUsed / currentUsage.reportsLimit) * 100)}%`,
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Plans */}
                <div className="grid gap-4 p-8 md:grid-cols-2">
                  {plans.map((plan) => {
                    const priceLabel = plan.priceMonthly != null
                      ? `${currencyFormatter.format(plan.priceMonthly)}`
                      : 'Custom pricing';
                    const periodLabel = plan.priceMonthly != null ? '/month' : '';
                    const isHighlighted = plan.slug === 'pro';
                    const ctaLabel = plan.isCustom || plan.priceMonthly == null
                      ? 'Talk to us'
                      : plan.slug === 'pro'
                      ? 'Upgrade to Pro'
                      : 'Upgrade';

                    const features = plan.features ?? [];

                    return (
                    <div
                      key={plan.id}
                      className={`relative rounded-2xl border p-6 transition-all ${
                        isHighlighted
                          ? 'border-primary bg-primary/5 shadow-lg shadow-primary/10'
                          : 'border-white/20 bg-white/5 hover:border-white/40'
                      }`}
                    >
                      {isHighlighted && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-white">
                          Most Popular
                        </div>
                      )}

                      <div className="mb-4">
                        <h3 className="text-lg font-semibold text-foreground">{plan.name}</h3>
                        <div className="mt-2 flex items-baseline">
                          <span className="text-3xl font-bold text-foreground">{priceLabel}</span>
                          <span className="text-muted-foreground">{periodLabel}</span>
                        </div>
                      </div>

                      <ul className="mb-6 space-y-3">
                        {features.map((feature) => (
                          <li key={feature} className="flex items-start gap-2 text-sm">
                            <CheckIcon className="h-5 w-5 shrink-0 text-primary" />
                            <span className="text-muted-foreground">{feature}</span>
                          </li>
                        ))}
                      </ul>

                      <button
                        onClick={() => handleUpgrade(plan)}
                        className={`w-full rounded-xl py-3 text-sm font-semibold transition-all ${
                          isHighlighted
                            ? 'bg-primary text-white hover:bg-primary/90 shadow-lg shadow-primary/25'
                            : 'bg-white/10 text-foreground hover:bg-white/20'
                        }`}
                      >
                        {ctaLabel}
                      </button>
                    </div>
                    );
                  })}
                </div>

                {/* Footer */}
                <div className="border-t border-white/10 bg-white/5 px-8 py-4">
                  <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                    <SparklesIcon className="h-4 w-4" />
                    <span>All plans include AI-powered analysis and professional report generation</span>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default PaywallModal;
